import React, { FC } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@clearcover/atlas';
import { Column, FormInput, Row } from 'Shared/components';
import styles from './styles.module.css';
import { STATE_OPTIONS, VALID_US_STATES_TYPE } from 'Shared/constants/states';
import {
  drivers,
  driver_options,
} from 'QuoteApp/components/Home/testData/drivers';
import { policies } from 'QuoteApp/components/Home/testData/policies';
import { vehicles } from 'QuoteApp/components/Home/testData/vehicles';

interface QuickQuoteFormProps {
  onSubmit(quote: any): void;
}

const createBasicTestData = (state: string, driver: string) => {
  const vehicle = vehicles[Math.floor(Math.random() * vehicles.length)];
  const policy = policies[state];
  const primaryDriver = drivers[driver];
  return {
    policy,
    quote_metadata: {
      campaign_medium: 'Lorem ad exercitation',
      campaign_name: 'ABC123',
      quote_identifier: '54asdf54asd5f4a5s4df',
      quote_name: 'Basic',
      partner_unstructured_metadata: {},
    },
    vehicles: [vehicle],
    current_policy: {
      proof_of_insurance: true,
      carrier_name: 'state_farm',
      state: 'CO',
      policy_start_date: '2017-09-01',
      policy_end_date: '2018-05-01',
      term_in_months: 6,
      customer_loyalty_in_months: 120,
      policy: {
        coverages: {
          bodily_injury: {
            limit_enum: 'fifty_one_hundred',
          },
        },
      },
      insurance_status: {
        is_currently_insured: true,
      },
      drivers: [
        {
          driver_information: {
            first_name: primaryDriver.driver_information.first_name,
            last_name: primaryDriver.driver_information.last_name,
            relationship_to_applicant: 'applicant',
          },
        },
      ],
    },
    drivers: [
      {
        exclude_from_quote: false,
        contact_information: {
          phone_numbers: [
            {
              number: '5555555555',
              type: 'home',
            },
          ],
        },
        confirmed_no_accidents: true,
        confirmed_no_violations: true,
        driver_information: {
          marital_status: 'single',
          relationship_to_applicant: 'applicant',
          ...primaryDriver.driver_information,
        },
        current_education: {
          education_level_clearcover_key: 'bachelors_degree',
        },
        license: {
          region: 'usa',
          age_first_licensed: 16,
          status: 'active',
          source: 'user_reported',
          has_previous_suspension: false,
          ...primaryDriver.license,
        },
        homeownership: {
          homeowner: true,
          months_owned: 16,
        },
        traffic_school: {
          course_taken: false,
        },
        occupation: {
          occupation_clearcover_key:
            'Nursing Instructors and Teachers, Postsecondary',
        },
        military_service: {
          is_deployed: false,
          service_member: true,
          military_grade: 'O-4',
        },
      },
    ],
  };
};

const sorMap = {
  '3.0': ['CA'],
  '4.0 - Insuresoft': ['IL'],
  '4.0': ['OH', 'TX', 'WI', 'LA'],
  '4.0 - Auto 2.0': ['AZ', 'OK', 'UT'],
  'Core - Auto 2.0': ['NE', 'PA', 'IN', 'MS'],
};

export const QuickQuoteForm: FC<QuickQuoteFormProps> = props => {
  const validation = Yup.object({
    state: Yup.string().required('Required'),
    driver: Yup.string().required('Required'),
  });

  const states = Object.keys(STATE_OPTIONS)
    .filter(state => Object.keys(policies).includes(state))
    .sort()
    .reduce((result: { [key: string]: string }, key: string) => {
      const sor = Object.entries(sorMap).reduce((acc, [k, v]) => {
        if (v.includes(key)) {
          acc = k;
        }
        return acc;
      }, 'Core');
      result[key] = `${STATE_OPTIONS[key as VALID_US_STATES_TYPE]} - ${sor}`;
      return result;
    }, {});

  return (
    <Formik
      initialValues={{
        state: 'NE',
        driver: 'tammy_behner',
      }}
      validationSchema={validation}
      onSubmit={values => {
        const quoteData = createBasicTestData(values.state, values.driver);
        props.onSubmit(quoteData);
      }}
    >
      {formik => {
        return (
          <Row className={styles.formWrapper}>
            <Column variant="third">
              <FormInput
                variant="select"
                options={states}
                onChange={(value: string) =>
                  formik.setFieldValue('state', value)
                }
                value={formik.values.state}
                label="State"
                name="state"
                errorMessage={
                  formik.touched['state'] ? formik.errors['state'] : undefined
                }
              />
            </Column>
            <Column variant="third">
              <FormInput
                className={styles.input}
                variant="select"
                options={driver_options}
                onChange={(value: string) =>
                  formik.setFieldValue('driver', value)
                }
                value={formik.values.driver}
                label="Driver"
                name="driver"
                errorMessage={
                  formik.touched['driver'] ? formik.errors['driver'] : undefined
                }
              />
            </Column>
            <Column variant="third">
              <Button variant="primary" onClick={formik.submitForm}>
                Submit
              </Button>
            </Column>
          </Row>
        );
      }}
    </Formik>
  );
};
